import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useQuestionTemplates,
  useQuestionTemplateCategories,
  useQuestionTemplateMutations,
} from '../../hooks'
import type {
  QuestionTemplate,
  CreateQuestionTemplateRequest,
  UpdateQuestionTemplateRequest,
} from '../../types/case-container'

export interface OQTemplateManagerState {
  templates: QuestionTemplate[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  categories: string[]
  creating: boolean
  updating: boolean
  deleting: boolean
  mutationError: string | null
  clearError: () => void
  searchTerm: string
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>
  categoryFilter: string
  setCategoryFilter: React.Dispatch<React.SetStateAction<string>>
  showCreateModal: boolean
  setShowCreateModal: React.Dispatch<React.SetStateAction<boolean>>
  editingTemplate: QuestionTemplate | null
  setEditingTemplate: React.Dispatch<React.SetStateAction<QuestionTemplate | null>>
  filteredTemplates: QuestionTemplate[]
  groupedTemplates: Record<string, QuestionTemplate[]>
  handleCreate: (data: CreateQuestionTemplateRequest | UpdateQuestionTemplateRequest) => Promise<void>
  handleUpdate: (data: CreateQuestionTemplateRequest | UpdateQuestionTemplateRequest) => Promise<void>
  handleDelete: (id: string) => Promise<void>
}

export function useOQTemplateManager(): OQTemplateManagerState {
  const { t } = useTranslation('settings')
  const { templates, loading, error, refetch } = useQuestionTemplates()
  const { categories } = useQuestionTemplateCategories()
  const {
    create, update, remove,
    creating, updating, deleting,
    error: mutationError, clearError,
  } = useQuestionTemplateMutations()

  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<QuestionTemplate | null>(null)

  const filteredTemplates = templates.filter((tpl) => {
    const matchesSearch =
      !searchTerm ||
      (tpl.description ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      tpl.questionText.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesSearch && (categoryFilter === 'all' || tpl.category === categoryFilter)
  })

  const groupedTemplates = filteredTemplates.reduce<Record<string, QuestionTemplate[]>>(
    (acc, tpl) => {
      const cat = tpl.category ?? t('templates.uncategorised')
      acc[cat] ??= []
      acc[cat].push(tpl)
      return acc
    },
    {}
  )

  const handleCreate = useCallback(
    async (data: CreateQuestionTemplateRequest | UpdateQuestionTemplateRequest): Promise<void> => {
      const result = await create(data as CreateQuestionTemplateRequest)
      if (result) {
        setShowCreateModal(false)
        void refetch()
      }
    },
    [create, refetch]
  )

  const handleUpdate = useCallback(
    async (data: CreateQuestionTemplateRequest | UpdateQuestionTemplateRequest): Promise<void> => {
      if (!editingTemplate) return
      const result = await update(editingTemplate.id, data as UpdateQuestionTemplateRequest)
      if (result) {
        setEditingTemplate(null)
        void refetch()
      }
    },
    [editingTemplate, update, refetch]
  )

  const handleDelete = useCallback(
    async (id: string): Promise<void> => {
      if (confirm(t('templates.confirmDelete'))) {
        const success = await remove(id)
        if (success) void refetch()
      }
    },
    [t, remove, refetch]
  )

  return {
    templates, loading, error, refetch,
    categories, creating, updating, deleting, mutationError, clearError,
    searchTerm, setSearchTerm, categoryFilter, setCategoryFilter,
    showCreateModal, setShowCreateModal, editingTemplate, setEditingTemplate,
    filteredTemplates, groupedTemplates,
    handleCreate, handleUpdate, handleDelete,
  }
}
